import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { callApi, formatDate, formatMoney } from "../Helpers";
import { getInlineLoader } from "../Helpers";
import {
  MdCalendarToday,
  MdDirectionsBike,
  MdEdit,
  MdInventory2,
  MdNumbers,
  MdPayments,
  MdQrCode2,
} from "react-icons/md";

export default function View({ uuid }) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  async function init() {
    setReady(false);
    await Promise.all([getData()]);
    setReady(true);
  }

  async function getData() {
    await callApi("get-motorcycle-data", { uuid }).then((response) => {
      if (response.status === 1) {
        setData(response.data);
      } else {
        appContext.tellError("Could not fetch data");
      }
    });
  }

  async function publish() {
    setLoading(true);
    await callApi("publish-news", { uuid }).then((response) => {
      if (response.status === 1) {
        appContext.tellMessage("Successful");
        getData();
        appContext.getAllMotorcycles();
      } else {
        appContext.tellError(response.msg);
      }
    });
    setLoading(false);
  }

  async function unpublish() {
    setLoading(true);
    await callApi("unpublish-news", { uuid }).then((response) => {
      if (response.status === 1) {
        appContext.tellMessage("Successful");
        getData();
        appContext.getAllMotorcycles();
      } else {
        appContext.tellError(response.msg);
      }
    });
    setLoading(false);
  }

  const renderDetailField = (label, value, icon = null) => (
    <div className="col-md-6 mb-4">
      <label className="form-label">{label}</label>
      <h6 className="d-flex align-items-center gap-2 mb-0">
        {icon}
        <span>{value || "Not set"}</span>
      </h6>
    </div>
  );

  useEffect(() => {
    init();
  }, [uuid]);

  useEffect(() => {
    appContext.setShowOverlayLoader(loading);
    return () => {
      appContext.setShowOverlayLoader(false);
    };
  }, [loading]);

  if (ready && data) {
    const totalReceived = Number(data.totalReceived || 0);
    const allocatedCount = Number(data.allocatedCount || 0);
    const availableStock = Math.max(totalReceived - allocatedCount, 0);

    const pricingFields = [
      {
        label: "Motorcycle Model",
        value: data.model,
        icon: <MdDirectionsBike size={16} />,
      },
      {
        label: "Unit Price",
        value: formatMoney(data.price || 0),
        icon: <MdPayments size={16} />,
      },
      {
        label: "Total Received",
        value: totalReceived,
        icon: <MdInventory2 size={16} />,
      },
      {
        label: "Allocated Count",
        value: allocatedCount,
        icon: <MdNumbers size={16} />,
      },
    ];

    const systemFields = [
      {
        label: "Database ID",
        value: data.id,
        icon: <MdNumbers size={16} />,
      },
      {
        label: "UUID",
        value: data.uuid,
        icon: <MdQrCode2 size={16} />,
      },
      {
        label: "Business Date",
        value: data.date ? formatDate(data.date) : "",
        icon: <MdCalendarToday size={16} />,
      },
      {
        label: "Date Created",
        value: data.dateCreated ? formatDate(data.dateCreated) : "",
        icon: <MdCalendarToday size={16} />,
      },
    ];

    return (
      <div className="container">
        <div className="detail-shell">
          <div className="detail-header">
            <div>
              <p className="app-page-eyebrow">Inventory profile</p>
              <h4 className="main-section-title mb-1">{data.model}</h4>
              <div className="d-flex align-items-center gap-3 flex-wrap text-muted small">
                <span>
                  <MdPayments size={14} /> {formatMoney(data.price || 0)}
                </span>
                <span>
                  <MdInventory2 size={14} /> {availableStock} available units
                </span>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-md-end">
              <span className={`status-pill ${data.status === "available" ? "status-pill-success" : "status-pill-muted"}`}>
                {data.status || "active"}
              </span>

              <button
                className="btn btn-dark btn-rounded app-cta-btn"
                onClick={() => {
                  appContext.navTo({
                    item: "view",
                    subItem: "create-motorcycle",
                    extraItem: data.uuid,
                  });
                }}
              >
                <MdEdit size={18} />
                Edit motorcycle
              </button>
            </div>
          </div>

          <div className="app-form-summary-grid mt-4">
            <div className="app-form-summary-card">
              <span>Total Received</span>
              <strong>{totalReceived} units</strong>
            </div>

            <div className="app-form-summary-card">
              <span>Allocated</span>
              <strong>{allocatedCount} units</strong>
            </div>

            <div className="app-form-summary-card">
              <span>Available Stock</span>
              <strong>{availableStock} units</strong>
            </div>
          </div>

          <div className="detail-actions-card mt-4">
            <h6 className="card-title mb-4">Inventory Details</h6>
            <div className="row">
              {pricingFields.map((field) => renderDetailField(field.label, field.value, field.icon))}
            </div>
          </div>
          
        </div>
      </div>
    );
  }

  return <div className="container mSupportLoading">{getInlineLoader()}</div>;
}
